<template>
  <label
    :class="allClasses"
    tabindex="0"
    v-on="listeners"
    @keydown.enter.space.stop.prevent="delegateInput"
  >
    <input
      ref="inputElement"
      :value="value"
      :checked="checked"
      v-bind="{ disabled, required, name, ...$attrs }"
      class="radio__input"
      type="radio"
      tabindex="-1"
      data-test-id="radio-input"
      @click="onInput"
    >
    <span class="radio__label"><slot /></span>
  </label>
</template>

<script>
import classes from '~~/common/vue/mixins/classes';

/**
 * The radio button is a custom wrapper around the native browser
 * input, and include it's label. There is no use case for a single
 * radio button component to be used. The radio button group should
 * be always used.
 */
export default {
  name: 'ERadio',
  mixins: [classes],
  model: {
    prop: 'modelValue',
    event: 'input',
  },
  props: {
    modelValue: {
      type: String,
      default: null,
    },
    value: {
      type: String,
      default: null,
    },
    name: {
      type: String,
      default: null,
    },
    size: {
      type: String,
      default: 'm',
      cssClass: true,
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
      cssClass: true,
    },
  },
  computed: {
    listeners() {
      const { input, ...rest } = this.$listeners;
      return rest;
    },
    allClasses() {
      const allClasses = [...this.classes];
      if (this.checked) {
        allClasses.push('radio--checked');
      }
      return allClasses;
    },
    checked() {
      return this.modelValue === this.value;
    },
  },
  methods: {
    onInput({ target: { value } }) {
      this.$emit('input', value);
    },
    delegateInput() {
      this.$refs.inputElement.click();
    },
  },
};
</script>

<style lang="scss" scoped>
$radio-size-m: $space-l;
$radio-size-s: 16px;
$radio-size-s-dot-size: 6px;
$radio-size-dot-size: 7px;
$tappable-area-padding: $space-1s;

.radio {
  display: flex;
  position: relative;
  font-family: $family;
  line-height: $line-height-m;
  color: var(--color-text-primary);
  cursor: pointer;
  padding-top: $space-1s;
  padding-bottom: $space-1s;

  &::before {
    content: "";
    display: block;
    border-radius: $border-radius-full;
    border: solid $border-width var(--color-form-border-default);
    box-sizing: border-box;
    pointer-events: none;
    transition: border $timing-2s $easing;
    flex-shrink: 0;
  }

  &:hover {
    &::before {
      border-color: var(--color-form-border-hover);
    }
  }

  &:focus {
    @extend %keyboard-nav-focus;

    &::before {
      border-color: var(--color-form-border-primary-active);
    }
  }
}

.radio__input {
  border: none;
  cursor: pointer;
  height: calc(100% + #{2 * $tappable-area-padding});
  left: -1 * $tappable-area-padding;
  margin: 0;
  opacity: 0;
  position: absolute;
  top: -1 * $tappable-area-padding;
}

.radio--size-m {
  font-size: $font-l;

  &::before {
    width: $radio-size-m;
    height: $radio-size-m;
    margin-right: $space-m;
  }

  .radio__input {
    width: calc(#{$radio-size-m} + #{2 * $tappable-area-padding});
  }
}

.radio--size-s {
  font-size: $font-m;

  &::before {
    width: $radio-size-s;
    height: $radio-size-s;
    margin-right: 7px;
  }

  &.radio--active {
    &::before {
      border-width: $radio-size-s-dot-size;
      border-color: var(--color-brand-primary);
    }
  }

  .radio__input {
    width: calc(#{$radio-size-s} + #{2 * $tappable-area-padding});
  }
}

.radio--checked {
  &::before,
  &:hover::before {
    border-width: $radio-size-dot-size;
    border-color: var(--color-brand-primary);
  }
}

.radio--disabled {
  pointer-events: none;
  color: var(--color-disabled);

  &::before {
    border-color: var(--color-disabled);
  }

  &.radio--active {
    &::before {
      border-color: var(--color-brand-primary);
    }
  }
}

.radio__label {
  flex-grow: 1;
}
</style>
